import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import ENDPOINTS from "../Constants/ApiEndpoints";
import { useLocation, useNavigate } from "react-router-dom";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const navigate = useNavigate();

    const locationRef = useRef(location);
    const isAuthenticatedRef = useRef(isAuthenticated);
    const isLoggingOutRef = useRef(false);

    useEffect(() => {
        locationRef.current = location;
    }, [location]);

    useEffect(() => {
        isAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated]);

    // Comprehensive logout: clears backend cookie and updates auth state
    const logout = useCallback(async (isExpired = false) => {
        if (isLoggingOutRef.current) return;
        isLoggingOutRef.current = true;

        try {
            await fetch(`${ENDPOINTS.LOGOUT}`, {
                method: "POST",
                credentials: "include",
            });
        } catch (err) {
            console.error("Logout error:", err);
        } finally {
            setIsAuthenticated(false);
            setUserData(null);
            isLoggingOutRef.current = false;
            navigate("/login", {
                replace: true,
                state: {
                    from: locationRef.current?.pathname !== "/login" ? locationRef.current : undefined,
                    sessionExpired: isExpired,
                },
            });
        }
    }, [navigate]);

    // Validate current session against /validate endpoint
    const validateSession = useCallback(async (isBackgroundCheck = false) => {
        try {
            const res = await fetch(`${ENDPOINTS.VALIDATE}`, {
                method: "POST",
                credentials: "include", // Send HTTP-only cookie
            });

            if (res.ok) {
                const data = await res.json();
                setIsAuthenticated(true);
                setUserData(data);
            } else {
                // Token is invalid or expired
                if (isAuthenticatedRef.current || isBackgroundCheck) {
                    console.warn("Session validation failed. Token expired or invalid. Logging out automatically.");
                    await logout(true);
                } else {
                    setIsAuthenticated(false);
                    setUserData(null);
                }
            }
        } catch (err) {
            console.error("Session validation error:", err);
            if (isAuthenticatedRef.current || isBackgroundCheck) {
                await logout(true);
            } else {
                setIsAuthenticated(false);
                setUserData(null);
            }
        } finally {
            setLoading(false);
        }
    }, [logout]);

    // Initial session check on app load
    useEffect(() => {
        const publicPaths = ["/login", "/"];
        const isPublic = publicPaths.includes(location.pathname);

        if (!isPublic) {
            validateSession();
        } else {
            setLoading(false);
        }
    }, [location.pathname, validateSession]);

    // Session revalidation on route change
    useEffect(() => {
        const publicPaths = ["/login", "/logout"];
        if (!publicPaths.includes(location.pathname)) {
            validateSession();
        }
    }, [location.pathname, validateSession]);

    // Listen for global 401 session expiration event dispatched by axios/fetch interceptors
    useEffect(() => {
        const handleSessionExpired = () => {
            if (isAuthenticatedRef.current && !isLoggingOutRef.current) {
                console.warn("Session expired event caught by AuthProvider. Triggering automatic logout.");
                logout(true);
            }
        };

        window.addEventListener("auth:session-expired", handleSessionExpired);
        return () => window.removeEventListener("auth:session-expired", handleSessionExpired);
    }, [logout]);

    // Periodic heartbeat validation (every 60s) & tab focus check to detect expiry while idle
    useEffect(() => {
        if (!isAuthenticated) return;

        // Check every 60 seconds while user is authenticated and tab is open
        const interval = setInterval(() => {
            if (!document.hidden) {
                validateSession(true);
            }
        }, 60000);

        // Check immediately when user switches back to this tab / window
        const handleFocusOrVisibility = () => {
            if (document.visibilityState === "visible" && !document.hidden) {
                validateSession(true);
            }
        };

        window.addEventListener("focus", handleFocusOrVisibility);
        document.addEventListener("visibilitychange", handleFocusOrVisibility);

        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", handleFocusOrVisibility);
            document.removeEventListener("visibilitychange", handleFocusOrVisibility);
        };
    }, [isAuthenticated, validateSession]);

    const login = (userInfo) => {
        setIsAuthenticated(true);
        setUserData(userInfo);
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, userData, login, logout, validateSession }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

// Hook to use auth
export const useAuth = () => useContext(AuthContext);

